import { api } from './api'
import type {
  MeterReading,
  MeterReadingImportPreviewResult,
  MeterReadingImportResult,
  MeterReadingImportRowInput,
  PlotReadingStatus,
} from '@riverpark/types'

export async function listPlotReadingStatuses(
  utilityTypeId: string,
  billingPeriod: string,
): Promise<{ data: PlotReadingStatus[] }> {
  const params = new URLSearchParams({ utilityTypeId, billingPeriod })
  return api.get<{ data: PlotReadingStatus[] }>(`/utilities/readings?${params.toString()}`)
}

export interface SaveReadingInput {
  utilityTypeId: string
  plotId: string
  billingPeriod: string
  previousReading: number
  currentReading: number
  photo?: File
  notes?: string
}

export async function saveDraftReading(input: SaveReadingInput): Promise<{ data: MeterReading }> {
  const form = new FormData()
  form.set('utilityTypeId', input.utilityTypeId)
  form.set('plotId', input.plotId)
  form.set('billingPeriod', input.billingPeriod)
  form.set('previousReading', String(input.previousReading))
  form.set('currentReading', String(input.currentReading))
  if (input.photo) form.set('photo', input.photo)
  if (input.notes) form.set('notes', input.notes)

  return api.postForm<{ data: MeterReading }>('/utilities/readings', form)
}

export interface UpdateReadingInput {
  previousReading?: number
  currentReading?: number
  photo?: File
  notes?: string
}

export async function updateDraftReading(
  id: string,
  input: UpdateReadingInput,
): Promise<{ data: MeterReading }> {
  const form = new FormData()
  if (input.previousReading !== undefined)
    form.set('previousReading', String(input.previousReading))
  if (input.currentReading !== undefined) form.set('currentReading', String(input.currentReading))
  if (input.notes !== undefined) form.set('notes', input.notes)
  if (input.photo) form.set('photo', input.photo)

  return api.patchForm<{ data: MeterReading }>(`/utilities/readings/${id}`, form)
}

export function readingPhotoUrl(id: string): string {
  const base = import.meta.env.VITE_API_URL?.replace(/\/$/, '') ?? '/api'
  return `${base}/utilities/readings/${id}/photo`
}

export async function attachReadingPhoto(id: string, photo: File): Promise<{ data: MeterReading }> {
  const form = new FormData()
  form.set('photo', photo)
  return api.postForm<{ data: MeterReading }>(`/utilities/readings/${id}/attach-photo`, form)
}

export interface MeterReadingImportRequest {
  utilityTypeId: string
  billingPeriod: string
  rows: MeterReadingImportRowInput[]
}

export async function previewMeterReadingImport(
  input: MeterReadingImportRequest,
): Promise<{ data: MeterReadingImportPreviewResult }> {
  return api.post<{ data: MeterReadingImportPreviewResult }>(
    '/utilities/readings/import/preview',
    input,
  )
}

export async function confirmMeterReadingImport(
  input: MeterReadingImportRequest,
): Promise<{ data: MeterReadingImportResult }> {
  return api.post<{ data: MeterReadingImportResult }>('/utilities/readings/import', input)
}
